import * as d3 from 'd3';
import * as moment from 'moment';
import * as _ from 'lodash';

import { RoundingHelper, ILogger, LoggerFactory, getId } from '../../helpers/helpers';
import { ISeries } from './series/ISeries';

export class Crosshair<T> {
    private _crosshair: d3.Selection<any>;
    private _date: Date;
    private _dispatch: any;
    private _filteredData: any[];
    private _group: d3.Selection<any>;
    private _height: number;
    private _id: string;
    private _logger: ILogger;
    private _rect: d3.Selection<any>;
    private _scale: d3.time.Scale<any, any>;
    private _seriesList: ISeries[];
    private _textGroup: d3.Selection<any>;
    private _textGroupWidth: number;
    private _textLineHeight: number;
    private _translateX: number;
    private _translateY: number;
    private _width: number;

    constructor(width: number, height: number, private container: d3.Selection<any>) {
        this._logger = LoggerFactory.getLogger('WideTech.Charting.Crosshair', false, 2);
        this._seriesList = [];
        this._width = width;
        this._height = height;
        this._textLineHeight = 17;
        this._textGroupWidth = 200;
        this._translateX = 0;
        this._translateY = 0;
        this.init(container);
    }

    private init(container) {
        this._logger.info('Initializing crosshair.');
        this._id = getId(`crosshairClipPath`);
        this._group = container.append('g')
            .classed('crosshair-container', 'true')
            .style('visibility', 'visible')
            .attr('data-clippathid', this._id);
        this._group
            .append('defs')
            .append('clipPath')
            .attr('id', this._id)
            .append('rect')
            .classed('crosshair-clippath', true)
            .attr({
                'width': isNaN(this._textGroupWidth) ? 0 : this._textGroupWidth,
                'height': isNaN(this._textLineHeight) ? 0 : this._textLineHeight
            });
        this._rect = container.append('rect')
            .classed('crosshair-rectangle', true)
            .attr({
                'width': this._width,
                'height': this._height
            })
            .on('mouseover', this.mouseOver())
            .on('mouseout', this.mouseOut())
            .on('mousemove', this.mouseMove());
        this._textGroup = this._group
            .append('g')
            .style('clip-path', `url("#${this._id}")`);
        this._textGroup
            .append('rect')
            .attr({
                'width': this._textGroupWidth,
                'height': this._textLineHeight
            })
            .classed('crosshair-text-group', true);
        this._crosshair = this._group.append('rect')
            .attr('width', 1)
            .attr('height', this._height)
            .classed('crosshair', true);
        this._textGroup
            .append('g')
            .classed('text-labels', true)
            .attr('transform', `translate(${this._textGroupWidth - 10},${0})`)
            .append('text')
            .classed('crosshair-text', true)
            .classed('crosshair-timestamp', true)
            .attr({
                'x': 2,
                'y': 12,
                'dy': '.3em'
            });
        this._dispatch = d3.dispatch('crosshair');
        this._logger.info('Crosshair initialized.');
    }

    setDate(date: Date) {
        this._date = date;
        this.updateCrosshair();
    }

    hideRect() {
        this._textGroup
            .classed('hidden-group', true);
    }

    private updateCrosshair() {

        let visible = 'hidden';

        if (this._scale && this._date != null) {
            const positionX = this._scale(this._date);
            if (!isNaN(positionX)) {
                try {

                    // find translation from date
                    //this._group.style('display', null);
                    visible = 'visible';

                    //let date = this.scale().invert(mouseX);
                    let translateX = -this._textGroupWidth;
                    if (positionX - this._textGroupWidth < 0) {
                        translateX = 0;
                    }
                    this._textGroup
                        .transition()
                        .duration(50)
                        .attr('transform', `translate(${translateX},${this._translateY})`);

                    const tX = RoundingHelper.notNaN(positionX + this._translateX, 0);
                    this._group
                        .attr({
                            'transform': `translate(${tX},${0})`
                        });

                    const bisect = d3.bisector((d: any) => new Date(d.timeStamp)).left;
                    this.setDateInCrosshair(this._date, bisect);

                } catch (e) {
                    this._logger.error(e);
                }
            }
        }

        this._group.style('visibility', visible);
    }

    private mouseOver(): { (data: any, index?: number): void } {
        const self = this;
        return () => {
            if (self._seriesList.length > 0) {
                self._group.style('visibility', 'visible');
            }
        };
    }

    private mouseMove(): { (data: any, index?: number): void } {
        const self = this;
        return () => {
            if (self._seriesList.length > 0) {
                const target = (<Event>d3.event).target;
                const mouseX = d3.mouse(target)[0];
                let translateX = -self._textGroupWidth;
                if (mouseX - self._textGroupWidth < 0) {
                    translateX = 0;
                }
                self._textGroup
                    .transition()
                    .duration(50)
                    .attr('transform', `translate(${translateX},${self._translateY})`);
                self._group
                    .attr({
                        'transform': `translate(${RoundingHelper.notNaN(mouseX + self._translateX, 0)},${0})`
                    });

                let date = self.scale().invert(mouseX);
                try {
                    const bisect = d3.bisector((d: any) => new Date(d.timeStamp)).left;
                    if (!self._filteredData) {
                        self.filterData();
                    }
                    const filtered = self._filteredData;
                    const value = this.getValue(bisect, filtered, date);
                    if (value) {
                        const timeStamp = value.timeStamp;
                        date = new Date(timeStamp);
                        self._date = date;
                    }
                    self._date = date;
                    try {
                        this.setDateInCrosshair(date, bisect);
                        this._dispatch.crosshair(date);
                    } catch (ex) {
                        this._logger.error(ex);
                    }
                } catch (e) {
                    this._logger.error(e);
                }
            }
        };
    }

    private getValue(bisect: (array: any[], x: Date, lo?: number, hi?: number) => number, filtered: any[], date: Date) {
        const i = bisect(filtered, date);
        const d0 = i > 0 ? filtered[i - 1] : filtered[i];
        const d1 = filtered[i];
        let value;
        if (d0 && d1) {
            value = moment(date).diff(moment(d0.timeStamp)) > moment(d1.timeStamp).diff(moment(date))
                ? d1
                : d0;
        } else if (d0) {
            value = d0;
        } else {
            value = d1;
        }
        return value;
    }

    private setDateInCrosshair(date: Date, bisect: any) {
        const dateFormatter = d3.time.format('%Y-%m-%d %H:%M');
        const valueFormatter = d3.format('.2f');
        const timeStamp = this._textGroup.select('.crosshair-timestamp');
        timeStamp.text(`Time stamp: ${dateFormatter(date)}`);
        this._textGroup.selectAll('.crosshair-series')
            .data(this._seriesList)
            .style({ 'fill': d => d.color() })
            .text(d => d.crosshairLabel(bisect, date, valueFormatter));
    }

    private mouseOut(): { (data: any, index?: number): void } {
        const self = this;
        return () => {
            self._group.style('visibility', 'hidden');
            this._date = null;
            this._dispatch.crosshair(this._date);
        };
    }

    draw(_translateX: number, _translateY: number, _container, _data) {
        this.filterData();
    }

    resize(width: number, height: number) {
        this._width = width;
        this._height = height;
        this._rect
            .attr({
                'width': this._width < 0 ? 0 : this._width,
                'height': this._height < 0 ? 0 : this._height
            });
        this._crosshair
            .attr({
                'height': this._height < 0 ? 0 : this._height
            });

    }

    translate(translateX, translateY) {
        this._translateX = !!translateX ? translateX : 0;
        this._translateY = !!translateY ? translateY : 0;
        this._textGroup.attr('transform', `translate(${0},${this._translateY})`);
        this._crosshair.attr('y', this._translateY);
        this._rect
            .attr({
                'x': 0,
                'y': 0,
                'transform': `translate(${this._translateX},${this._translateY})`
            });

    }

    scale(value?) {
        if (value) {
            this._scale = value;
            this.updateCrosshair();
        }
        return this._scale;
    }

    addSeries(series: ISeries): void {
        if (!series.isCrosshairHidden) {
            this._logger.info('Adding series.');
            this._seriesList.push(series);
            this.resizeRectangles();

            this._textGroup
                .select('.text-labels')
                .selectAll('.crosshair-series')
                .data(this._seriesList)
                .enter()
                .append('text')
                .classed('crosshair-text', true)
                .classed('crosshair-series', true)
                .attr({
                    'x': 3,
                    'y': (d, i) => (i + 2) * this._textLineHeight,
                    'dy': '.3em'
                })
                .style('fill', d => d.color());
        }
        this.filterData();

    }

    updateSeries(series: ISeries): void {
        this._logger.info('Updating series.');
        if (_.every(this._seriesList, s => s.identifier() !== series.identifier())) {
            this.addSeries(series);
        } else if ((series.isCrosshairHidden) && _.some(this._seriesList, s => s.identifier()
            === series
                .identifier())) {
            this.removeSeries(series);
        }
        this.filterData();
    }

    removeSeries(series: ISeries): void {
        this._logger.info('Removing series.');
        for (let i = 0; i < this._seriesList.length; i++) {
            const currentSeries = this._seriesList[i];
            if (currentSeries.identifier() === series.identifier()) {
                this._seriesList.splice(i, 1);
                break;
            }
        }
        this._textGroup.selectAll('.crosshair-series')
            .data(this._seriesList)
            .exit()
            .remove();
        this.resizeRectangles();
        this.filterData();
    }

    update(_data) {
        this.updateCrosshair();
        this.filterData();
    }

    private filterData() {
        try {
            if (this._seriesList) {
                const visibleSeries = this._seriesList.filter(s => s.isCrosshairHidden !== true);
                this._filteredData = [].concat(...visibleSeries.map(s => s.data()));
            }
        } catch (e) {
            this._logger.error(e.message);
        }
    }

    enable() {
        this._group.classed('disabled', false);
    }

    disable() {
        this._group.classed('disabled', true);
    }

    onCrosshair(callback: any) {
        this._dispatch.on('crosshair', callback);
    }

    private resizeRectangles() {
        const height = this._textLineHeight * (this._seriesList.length + 1) + 10;
        this._textGroup
            .select('.crosshair-text-group')
            .attr({
                'height': height
            });
        this._group
            .select('.crosshair-clippath')
            // .select('rect')
            .attr({
                'height': height
            });
    }

}